import axios from 'axios';
import { getApiBaseUrl } from './apiBaseUrl';
import { triggerSessionExpired } from './sessionRecovery';

const api = axios.create({
  baseURL: `${getApiBaseUrl()}/api`,
});

api.interceptors.request.use((config) => {
  config.baseURL = `${getApiBaseUrl()}/api`;
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      error.__sessionExpired = true;
      triggerSessionExpired({ reason: 'Your session expired. Log in again to continue without losing your changes.' });
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const getCustomersPaginated = (params) => api.get('/customers/paginated', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const updateCustomerNo = (id, customerNo) => api.put(`/customers/${id}/customer-no`, { customer_no: customerNo });
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const createOrRetryCustomerDriveFolder = (id) => api.post(`/customers/${id}/drive-folder`);
export const getNextCustomerNumber = () => api.get('/customers/next-number');
export const getCustomerNumberingSettings = () => api.get('/customers/settings/numbering');
export const updateCustomerNumberingSettings = (payload) => api.put('/customers/settings/numbering', payload);
export const bulkDeleteCustomers = (customerIds) => api.post('/customers/bulk-delete', customerIds);
export const bulkImportCustomers = (customers) => api.post('/customers/bulk-import', customers);
export const bootstrapCustomerMappingReview = (payload) => api.post('/customers/mapping/bootstrap', payload);
export const getCustomerMappingReviewSummary = () => api.get('/customers/mapping/summary');
export const getCustomerMappingReviewItems = (params) => api.get('/customers/mapping/review', { params });
export const searchCustomerMappingReference = (query, limit = 20) =>
  api.get('/customers/mapping/reference/search', { params: { query, limit } });
export const assignCustomerMappingReview = (reviewId, payload) =>
  api.post(`/customers/mapping/review/${reviewId}/assign`, payload);
export const skipCustomerMappingReview = (reviewId, payload = {}) =>
  api.post(`/customers/mapping/review/${reviewId}/skip`, payload);
export const getMeasurementImportPriority = () => api.get('/customers/measurement-import-priority');
export const getCustomerNoSyncSummary = () => api.get('/customers/customer-no-sync/summary');
export const bulkSyncCustomerNumbersFromSheet = (customerIds) => api.post('/customers/customer-no-sync/bulk', { customer_ids: customerIds });
export const backfillCustomerNumbersFromSheet = (payload = {}) => api.post('/customers/customer-no-sync/backfill', payload);
export const syncOrderToDailyStatus = (orderId) => api.post('/orders/sync-daily-status', { order_id: orderId });

// Google Sheet Sync (production + fabric)
export const runGoogleSheetSyncDryRun = (payload) => api.post('/google-sheet-sync/dry-run', payload);
export const getGoogleSheetSyncRun = (runId) => api.get(`/google-sheet-sync/runs/${runId}`);
export const applyGoogleSheetSyncRun = (payload) => api.post('/google-sheet-sync/apply', payload);

// Orders
export const getOrders = (params) => api.get('/orders', { params });
export const getOrdersPaginated = (params) => api.get('/orders/paginated', { params });
export const getOrdersPaginatedLite = (params, config = {}) => api.get('/orders/paginated-lite', { params, ...config });
export const searchOrders = (query, limit = 50) => api.get('/orders/search', { params: { query, limit } });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const getOrderFull = (id, params = {}) => api.get(`/orders/${id}/full`, { params });
export const getOrderEditBootstrap = (id) => api.get(`/orders/${id}/edit-bootstrap`);
export const getOrderLineItemsForCopy = (id) => api.get(`/orders/${id}/line-items-for-copy`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const previewLineItemFabricChange = (orderId, lineItemId, data) =>
  api.post(`/orders/${orderId}/line-items/${lineItemId}/fabric-change/preview`, data);
export const applyLineItemFabricChange = (orderId, lineItemId, data) =>
  api.post(`/orders/${orderId}/line-items/${lineItemId}/fabric-change/apply`, data);
export const bulkCreateTestGarments = (orderId, data) =>
  api.post(`/orders/${orderId}/test-garments/bulk`, data);
export const updateOrderStatus = (id, status) => api.patch(`/orders/${id}/status`, null, { params: { status } });
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const bulkDeleteOrders = (orderIds) => api.post('/orders/bulk-delete', orderIds);
export const getWooOrdersPreview = (params) => api.get('/orders/woo/preview', { params });
export const importWooOrder = (payload) => api.post('/orders/woo/import', payload);
export const runAdminWooReconcileDryRun = (payload) => api.post('/admin/woo-reconcile/dry-run', payload);
export const runAdminWooReconcileApply = (payload) => api.post('/admin/woo-reconcile/apply', payload);
export const runCustomerMasterBackfill = () => api.post('/admin/customer-master/backfill', {});
export const getParentOrders = (search = '', days = 120) => api.get('/orders/parent-orders', { params: { search, days } });
export const getOrderHistory = (orderId) => api.get(`/orders/${orderId}/history`);

// Order Interactions
export const getOrderInteractions = (orderId) => api.get('/order-interactions', { params: { order_id: orderId } });
export const createOrderInteraction = (data) => api.post('/order-interactions', data);
export const updateOrderInteraction = (id, data) => api.put(`/order-interactions/${id}`, data);
export const deleteOrderInteraction = (id) => api.delete(`/order-interactions/${id}`);

// Alterations
export const getAlterations = (orderId) => api.get('/alterations', { params: { order_id: orderId } });
export const createAlteration = (data) => api.post('/alterations', data);
export const updateAlteration = (id, data) => api.put(`/alterations/${id}`, data);
export const updateAlterationStatus = (id, status) => api.patch(`/alterations/${id}/status`, null, { params: { status } });
export const deleteAlteration = (id) => api.delete(`/alterations/${id}`);

// Garments
export const getGarments = (params) => api.get('/garments', { params });
export const getGarmentsPaginated = (params, config = {}) => api.get('/garments/paginated', { params, ...config });
export const getGarmentDispatchDateOptions = (params) => api.get('/garments/dispatch-date-options', { params });
export const getGarmentPlanStatus = (garment_ids) => api.post('/garments/plan-status', { garment_ids });
export const getGarment = (id) => api.get(`/garments/${id}`);
export const createGarment = (data) => api.post('/garments', data);
export const updateGarment = (id, data) => api.put(`/garments/${id}`, data);
export const updateGarmentStatus = (id, status) => api.patch(`/garments/${id}/status`, null, { params: { status } });
export const deleteGarment = (id) => api.delete(`/garments/${id}`);

// Muslin inventory
export const getMuslinInventory = (params = {}) => api.get('/muslin-inventory', { params });
export const previewMuslinInventoryImport = (payload) => api.post('/muslin-inventory/import/preview', payload);
export const applyMuslinInventoryImport = (payload) => api.post('/muslin-inventory/import/apply', payload);
export const createMuslinInventoryItem = (payload) => api.post('/muslin-inventory', payload);
export const updateMuslinInventoryItem = (id, payload) => api.put(`/muslin-inventory/${id}`, payload);
export const deleteMuslinInventoryItem = (id) => api.delete(`/muslin-inventory/${id}`);
export const bulkDeleteMuslinInventoryItems = (muslin_inventory_ids) => api.post('/muslin-inventory/bulk-delete', { muslin_inventory_ids });
export const bulkAssignMuslinInventoryStore = (muslin_inventory_ids, store) => api.post('/muslin-inventory/bulk-store', { muslin_inventory_ids, store });
export const previewMuslinDesignBackfill = (muslin_inventory_ids) => api.post('/muslin-inventory/design-backfill/preview', { muslin_inventory_ids });
export const applyMuslinDesignBackfill = (muslin_inventory_ids) => api.post('/muslin-inventory/design-backfill/apply', { muslin_inventory_ids });
export const previewMuslinFormAssist = (payload) => api.post('/muslin-inventory/form-assist', payload);
export const releaseMuslinInventoryItem = (id, payload = {}) => api.post(`/muslin-inventory/${id}/release`, payload);
export const getMuslinMatchRecommendations = (garmentId) => api.get(`/muslin-inventory/match/${garmentId}`);
export const recordMuslinDecision = (payload) => api.post('/muslin-inventory/decision', payload);

// House-stock inventory (owner-only for now)
export const getInventoryItems = (params = {}) => api.get('/inventory/items', { params });
export const getInventoryItem = (id) => api.get(`/inventory/items/${id}`);
export const lookupInventoryCode = (code) => api.get('/inventory/lookup', { params: { code } });
export const createInventoryItem = (payload) => api.post('/inventory/items', payload);
export const updateInventoryItem = (id, payload) => api.put(`/inventory/items/${id}`, payload);
export const deleteInventoryItem = (id) => api.delete(`/inventory/items/${id}`);
export const createInventoryMovement = (id, payload) => api.post(`/inventory/items/${id}/movements`, payload);
export const getInventoryMovements = (id) => api.get(`/inventory/items/${id}/movements`);
export const uploadInventoryImage = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/inventory/items/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Garment Images
export const getGarmentImages = (garmentId) => api.get(`/garments/${garmentId}/images`);
export const addGarmentImage = (garmentId, data) => api.post(`/garments/${garmentId}/images`, data);
export const updateGarmentImage = (garmentId, imageId, data) => api.patch(`/garments/${garmentId}/images/${imageId}`, data);
export const deleteGarmentImage = (garmentId, imageId) => api.delete(`/garments/${garmentId}/images/${imageId}`);
export const uploadGarmentImage = async (garmentId, file, imageType = 'reference') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('image_type', imageType);
  return api.post(`/garments/${garmentId}/images/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Fabric Orders
export const getFabricOrders = (status) => api.get('/fabric-orders', { params: { status } });
export const getFabricOrdersForProcurement = (params, config = {}) => api.get('/fabric-orders/procurement', { params, ...config });
export const getFabricOrderDetail = (id) => api.get(`/fabric-orders/${id}/detail`);
export const createFabricOrder = (data) => api.post('/fabric-orders', data);
export const updateFabricOrder = (id, data) => api.put(`/fabric-orders/${id}`, data);
export const inlineUpdateFabricOrder = (id, data) => api.patch(`/fabric-orders/${id}/inline-update`, data);
export const updateFabricOrderStatus = (id, status) => api.patch(`/fabric-orders/${id}/status`, null, { params: { status } });
export const bulkUpdateFabricStatus = (fabricIds, status) => api.post('/fabric-orders/bulk-status-update', fabricIds, { params: { status } });
export const bulkEditFabricOrders = (fabricIds, updates) => api.post('/fabric-orders/bulk-edit', fabricIds, { params: updates });
export const groupFabricOrders = (fabricIds) => api.post('/fabric-orders/group', fabricIds);
export const ungroupFabricOrders = (fabricIds) => api.post('/fabric-orders/ungroup', fabricIds);
export const deleteFabricOrder = (id) => api.delete(`/fabric-orders/${id}`);

// Suppliers
export const getSuppliers = () => api.get('/suppliers');
export const getSupplier = (id) => api.get(`/suppliers/${id}`);
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);
export const seedDefaultSuppliers = () => api.post('/suppliers/seed-defaults');

// Measurements
export const getMeasurements = (customerId) => api.get('/measurements', { params: { customer_id: customerId } });
export const getMeasurement = (id) => api.get(`/measurements/${id}`);
export const createMeasurement = (data) => api.post('/measurements', data);
export const updateMeasurement = (id, data) => api.put(`/measurements/${id}`, data);
export const deleteMeasurement = (id) => api.delete(`/measurements/${id}`);

// Patterns
export const getPatterns = (customerId, garmentType) => api.get('/patterns', { params: { customer_id: customerId, garment_type: garmentType } });
export const getPattern = (id) => api.get(`/patterns/${id}`);
export const findPatternSeed = (patternCode, garmentType) => api.get('/patterns', { params: { pattern_code: patternCode, garment_type: garmentType } });
export const createPattern = (data) => api.post('/patterns', data);
export const updatePattern = (id, data) => api.put(`/patterns/${id}`, data);
export const deletePattern = (id) => api.delete(`/patterns/${id}`);

// Pattern Revisions
export const getPatternRevisions = (patternId) => api.get('/pattern-revisions', { params: { pattern_id: patternId } });
export const getPatternRevision = (id) => api.get(`/pattern-revisions/${id}`);
export const createPatternRevision = (data) => api.post('/pattern-revisions', data);
export const updatePatternRevision = (id, data) => api.put(`/pattern-revisions/${id}`, data);
export const updatePatternRevisionStatus = (id, status) => api.patch(`/pattern-revisions/${id}/status`, null, { params: { status } });
export const deletePatternRevision = (id) => api.delete(`/pattern-revisions/${id}`);

// Fitting Sessions
export const getFittingSessions = (customerId, status) => api.get('/fitting-sessions', { params: { customer_id: customerId, status } });
export const getFittingSession = (id) => api.get(`/fitting-sessions/${id}`);
export const createFittingSession = (data) => api.post('/fitting-sessions', data);
export const updateFittingSession = (id, data) => api.put(`/fitting-sessions/${id}`, data);
export const completeFittingSession = (id) => api.patch(`/fitting-sessions/${id}/complete`);
export const deleteFittingSession = (id) => api.delete(`/fitting-sessions/${id}`);

// Fitting Photos
export const getFittingPhotos = (customerId, sessionId, patternId) => 
  api.get('/fitting-photos', { params: { customer_id: customerId, session_id: sessionId, pattern_id: patternId } });
export const uploadFittingPhoto = (formData) => api.post('/fitting-photos/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const updateFittingPhoto = (id, data) => api.patch(`/fitting-photos/${id}`, data);
export const rotateFittingPhoto = (id, data) => api.post(`/fitting-photos/${id}/rotate`, data);
export const deleteFittingPhoto = (id) => api.delete(`/fitting-photos/${id}`);

export const preparePerthImportWizard = (data) => api.post('/fitting-studio/perth-import/wizard/prepare', data);
export const applyPerthImportWizardBody = (data) => api.post('/fitting-studio/perth-import/wizard/apply-body', data);
export const applyPerthImportWizardPatternStage = (data) => api.post('/fitting-studio/perth-import/wizard/apply-pattern-stage', data);

// Garment Type Configs
export const getGarmentTypeConfigs = () => api.get('/garment-type-configs');
export const getGarmentTypeConfig = (id) => api.get(`/garment-type-configs/${id}`);
export const createGarmentTypeConfig = (data) => api.post('/garment-type-configs', data);
export const deleteGarmentTypeConfig = (id) => api.delete(`/garment-type-configs/${id}`);
export const seedDefaultGarmentTypeConfigs = () => api.post('/garment-type-configs/seed-defaults');

// Garment Specification Fields
export const getGarmentSpecificationFields = (configId) => api.get('/garment-specification-fields', { params: { garment_type_config_id: configId } });
export const createGarmentSpecificationField = (data) => api.post('/garment-specification-fields', data);
export const updateGarmentSpecificationField = (id, data) => api.put(`/garment-specification-fields/${id}`, data);
export const deleteGarmentSpecificationField = (id) => api.delete(`/garment-specification-fields/${id}`);
export const bulkEditGarmentSpecificationFields = (data) => api.post('/garment-specification-fields/bulk-edit', data);

// Body Measurement Fields
export const getBodyMeasurementFields = () => api.get('/body-measurement-fields');
export const createBodyMeasurementField = (data) => api.post('/body-measurement-fields', data);
export const updateBodyMeasurementField = (id, data) => api.put(`/body-measurement-fields/${id}`, data);
export const deleteBodyMeasurementField = (id) => api.delete(`/body-measurement-fields/${id}`);
export const seedDefaultBodyMeasurementFields = () => api.post('/body-measurement-fields/seed-defaults');

// Products
export const getProducts = () => api.get('/products');
export const getProduct = (id) => api.get(`/products/${id}`);
export const getProductWithCustomizations = (id) => api.get(`/products/${id}/customizations`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const seedDefaultProducts = () => api.post('/products/seed-defaults');
export const copyCustomizations = (targetProductId, sourceProductId, replaceExisting = false) => 
  api.post(`/products/${targetProductId}/copy-customizations`, null, { 
    params: { source_product_id: sourceProductId, replace_existing: replaceExisting } 
  });

// Customization Categories
export const getCustomizationCategories = (productId) => api.get('/customization-categories', { params: { product_id: productId } });
export const getCustomizationCategory = (id) => api.get(`/customization-categories/${id}`);
export const createCustomizationCategory = (data) => api.post('/customization-categories', data);
export const updateCustomizationCategory = (id, data) => api.put(`/customization-categories/${id}`, data);
export const deleteCustomizationCategory = (id) => api.delete(`/customization-categories/${id}`);

// Customization Options
export const getCustomizationOptions = (categoryId) => api.get('/customization-options', { params: { category_id: categoryId } });
export const getCustomizationOption = (id) => api.get(`/customization-options/${id}`);
export const createCustomizationOption = (data) => api.post('/customization-options', data);
export const updateCustomizationOption = (id, data) => api.put(`/customization-options/${id}`, data);
export const deleteCustomizationOption = (id) => api.delete(`/customization-options/${id}`);

// Payments
export const getPayments = (orderId) => api.get('/payments', { params: { order_id: orderId } });
export const createPayment = (data) => api.post('/payments', data);
export const updatePayment = (id, data) => api.put(`/payments/${id}`, data);
export const deletePayment = (id) => api.delete(`/payments/${id}`);

// Tasks
export const getTasks = (status, storeFilter) => api.get('/tasks', { params: { status, store_filter: storeFilter } });
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const updateTaskStatus = (id, status) => api.patch(`/tasks/${id}/status`, null, { params: { status } });
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Tailors
export const getTailors = () => api.get('/garments/production/tailors');
export const createTailor = (data) => api.post('/garments/production/tailors', data);
export const updateTailor = (id, data) => api.put(`/garments/production/tailors/${id}`, data);
export const deleteTailor = (id) => api.delete(`/garments/production/tailors/${id}`);
export const bulkUpdateTailors = (payload) => api.post('/garments/production/tailors/bulk-update', payload);

// Garment Task Configs
export const getGarmentTaskConfigs = () => api.get('/garments/production/task-configs');
export const createGarmentTaskConfig = (data) => api.post('/garments/production/task-configs', data);
export const updateGarmentTaskConfig = (id, data) => api.put(`/garments/production/task-configs/${id}`, data);
export const deleteGarmentTaskConfig = (id) => api.delete(`/garments/production/task-configs/${id}`);
export const seedGarmentTaskConfigs = () => api.post('/garments/production/task-configs/seed-defaults');

// Production Scheduler
export const getProductionOverview = () => api.get('/garments/production/overview');
export const getTailorWorkloads = () => api.get('/garments/production/workloads/tailors');
export const getPatternWorkloads = () => api.get('/garments/production/workloads/patterns');
export const autoAssignProduction = (apply = false) => api.post('/garments/production/auto-assign', null, { params: { apply } });
export const getProductionTasks = (params = {}) => api.get('/garments/production/tasks', { params });
export const updateProductionTask = (taskId, data) => api.patch(`/garments/production/tasks/${taskId}`, data);
export const bulkDeleteProductionTasks = (taskIds) => api.delete('/garments/production/tasks/bulk', { data: { task_ids: taskIds } });
export const moveProductionTask = (task_id, direction) => api.patch(`/garments/production/tasks/${task_id}/move`, { direction });
export const planPreview = (garment_ids) => api.post('/garments/production/plan-preview', { garment_ids });
export const applyAssignments = (assignments) => api.post('/garments/production/apply-assignments', { assignments });
export const getProductionAdHocOptions = (garment_no) => api.get('/garments/production/ad-hoc/options', { params: { garment_no } });
export const recommendProductionAdHocTask = (payload) => api.post('/garments/production/ad-hoc/recommend', payload);
export const createProductionAdHocTask = (payload) => api.post('/garments/production/tasks/ad-hoc', payload);

// Task Comments
export const getTaskComments = (taskId) => api.get(`/tasks/${taskId}/comments`);
export const createTaskComment = (taskId, data) => api.post(`/tasks/${taskId}/comments`, data);
export const updateTaskComment = (taskId, commentId, data) => api.put(`/tasks/${taskId}/comments/${commentId}`, data);
export const deleteTaskComment = (taskId, commentId) => api.delete(`/tasks/${taskId}/comments/${commentId}`);

// Users
export const getUsers = () => api.get('/users');
export const getAssignableUsers = () => api.get('/users/assignable');
export const getCurrentUser = () => api.get('/auth/me');

// Addresses
export const getAddresses = () => api.get('/addresses');
export const createAddress = (data) => api.post('/addresses', data);
export const deleteAddress = (id) => api.delete(`/addresses/${id}`);

// Emails
export const sendOrderInvoiceEmail = (orderId) => api.post('/emails/order-invoice', null, { params: { order_id: orderId } });
export const sendWelcomeEmail = (data) => api.post('/emails/welcome', data);
export const getEmailLogs = (params) => api.get('/emails/logs', { params });
export const getEmailBccSettings = () => api.get('/emails/bcc-settings');
export const updateEmailBccSettings = (storeKey, payload) => api.put(`/emails/bcc-settings/${storeKey}`, payload);

// Production Dashboard
export const getProductionKPIs = (params) => api.get('/production-dashboard/kpis', { params });

// Intercompany Costing
export const getIntercompanyModes = () => api.get('/intercompany/modes');
export const upsertIntercompanyMode = (payload) => api.put('/intercompany/modes', payload);
export const getIntercompanyRateCards = (params) => api.get('/intercompany/rate-cards', { params });
export const createIntercompanyRateCard = (payload) => api.post('/intercompany/rate-cards', payload);
export const updateIntercompanyRateCard = (rateCardId, payload) => api.put(`/intercompany/rate-cards/${rateCardId}`, payload);
export const getIntercompanyMonthlyRateGrid = (params) => api.get('/intercompany/monthly-rate-grid', { params });
export const upsertIntercompanyMonthlyRateGrid = (payload) => api.put('/intercompany/monthly-rate-grid', payload);
export const getIntercompanyDetailedRules = (params) => api.get('/intercompany/detailed-rules', { params });
export const createIntercompanyDetailedRule = (payload) => api.post('/intercompany/detailed-rules', payload);
export const generateIntercompanyStatement = (payload) => api.post('/intercompany/statements/generate', payload);
export const getIntercompanyStatements = (params) => api.get('/intercompany/statements', { params });
export const updateIntercompanyStatementStatus = (statementId, status) =>
  api.patch(`/intercompany/statements/${statementId}/status`, null, { params: { status } });
export const seedWiseRemittancesJun2025May2026 = (store) =>
  api.post('/intercompany/remittances/seed-wise-jun2025-may2026', null, { params: { store } });
export const getIntercompanyRemittances = (params) => api.get('/intercompany/remittances', { params });
export const upsertIntercompanyRemittances = (payload) => api.put('/intercompany/remittances', payload);
export const deleteIntercompanyRemittance = (entryId) => api.delete(`/intercompany/remittances/${entryId}`);
export const uploadIntercompanyRemittanceAttachment = async ({ store, month, file }) => {
  const formData = new FormData();
  formData.append('store', store);
  formData.append('month', month);
  formData.append('file', file);
  return api.post('/intercompany/remittances/upload-attachment', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const parseIntercompanyRemittancePdf = async ({ store, file }) => {
  const formData = new FormData();
  formData.append('store', store);
  formData.append('file', file);
  return api.post('/intercompany/remittances/parse-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const downloadIntercompanyRemittanceAttachment = async (entryId, fallbackName = 'remittance-attachment.pdf') => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getApiBaseUrl()}/api/intercompany/remittances/${entryId}/attachment`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    let detail = 'Failed to download attachment';
    try {
      const data = await response.json();
      detail = data?.detail || detail;
    } catch (e) {
      // no-op
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename=([^;]+)/i);
  const filename = filenameMatch ? filenameMatch[1].replace(/"/g, '') : fallbackName;
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
export const getIntercompanyReconciliation = (params) => api.get('/intercompany/reconciliation', { params });
export const autoGenerateIntercompanyDraftsForReconciliation = (params) =>
  api.post('/intercompany/reconciliation/auto-generate-drafts', null, { params });
export const seedIntercompanyFxJun2025May2026 = (store) =>
  api.post('/intercompany/fx/seed-jun2025-may2026', null, { params: { store } });
export const getIntercompanyFxRates = (params) => api.get('/intercompany/fx', { params });
export const upsertIntercompanyFxRate = (payload) => api.put('/intercompany/fx', payload);

export const downloadIntercompanyStatementInvoicePdf = async (statementId) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getApiBaseUrl()}/api/intercompany/statements/${statementId}/invoice/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    let detail = 'Failed to download invoice PDF';
    try {
      const data = await response.json();
      detail = data?.detail || detail;
    } catch (e) {
      // no-op
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const disposition = response.headers.get('Content-Disposition') || '';
  const fallback = `Intercompany-Invoice-${statementId}.pdf`;
  const filenameMatch = disposition.match(/filename=([^;]+)/i);
  const filename = filenameMatch ? filenameMatch[1].replace(/"/g, '') : fallback;
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default api;
